import { useCallback, useEffect, useRef, useState } from 'react';

const placeholderImages = {
    loading: 'https://i.imgur.com/FnSf0dL.jpg',
    upload: 'https://i.imgur.com/BdbjnMF.jpg',
    selectImage: 'https://i.imgur.com/jw1gtE8.png'
};

// Shared slide/image pipeline for the member and guest room pages. Callers
// feed it presentation items plus a loader that fetches an item's image blob
// (authenticated ImageService for members, GuestService for guests); the hook
// tracks downloaded images, the visible slide map, sort order, and which
// image the Vyur currently shows.
export default function useSlides(onLoadError) {
    const [slidesMap, setSlidesMap] = useState({});
    // Visible items the room has listed but whose image hasn't downloaded yet.
    // They aren't slides yet (nothing to show or navigate to), but the Image Q
    // draws a placeholder for each so a new slide doesn't stay invisible for
    // the whole download.
    const [loadingSlides, setLoadingSlides] = useState([]);
    const [sortAsc, setSortAsc] = useState(true);
    const [currentImg, setCurrentImg] = useState(document.createElement('img'));
    const [maxIndex, setMaxIndex] = useState(0);
    const [visibleIndices, setVisibleIndices] = useState([]);

    // Kinda like a cache so the same images aren't loaded over the network repeatedly.
    // Identity here (and in the pending/latest checks below) is itemName - the
    // unique storage key the blob is fetched by - never index: an index is only
    // ever a display position, and two items can briefly share one across a
    // refresh. (Indices are not recycled: maxIndex below counts soft-deleted
    // items too, so the next upload always takes a number no slide has held.)
    const loadedImagesRef = useRef([]);
    // itemNames with a download in flight. setItems is called on every room
    // refresh (e.g. every 5s guest heartbeat), so without this a slow
    // download can still be pending when the next tick fires — the
    // "already loaded" check below only sees loadedImagesRef, which isn't
    // populated until the fetch resolves, so it would start a duplicate.
    const pendingDownloadsRef = useRef(new Set());
    // Items from the most recent setItems call, so a download that resolves
    // late can tell whether its slide still exists and pick up metadata
    // changes (e.g. hidden) that happened while it was in flight. The member
    // page only refreshes on room events, so stale results inserted here
    // would otherwise linger indefinitely.
    const latestItemsRef = useRef([]);
    const [triggerNewSlidesMap, setTriggerNewSlidesMap] = useState(Math.random());

    const onLoadErrorRef = useRef(onLoadError);
    onLoadErrorRef.current = onLoadError;

    // Blob URLs live until the document dies, not the component, so revoke
    // them on unmount or every slide image ever loaded accumulates as the
    // user moves between rooms. disposedRef lets downloads that resolve
    // after this cleanup know they missed it and must not create a URL.
    const disposedRef = useRef(false);
    useEffect(() => {
        // Reset in the effect body, not just at init: StrictMode remounts
        // reuse the same refs, so a cleanup-only flag would stick at true.
        disposedRef.current = false;
        return () => {
            disposedRef.current = true;
            loadedImagesRef.current.forEach(({ burl }) => {
                if (burl) window.URL.revokeObjectURL(burl);
            });
        };
    }, []);

    // Memoization rule: only callbacks that consumers depend on by identity
    // are useCallback'd — setItems is an effect dependency in GuestRoom, and
    // updateImage is passed into PresentationDrawer. The other helpers are
    // deliberately recreated per render; don't put them in dependency arrays.
    const setItems = useCallback((presentationItems = [], loadBlob) => {
        latestItemsRef.current = presentationItems || [];
        setMaxIndex(
            (presentationItems || []).map(({ index }) => +index).reduce((a, i) => Math.max(a, i), 0)
        );

        // Don't request data from backend for images that are not visible or have been pulled already
        const imagesToShow = (presentationItems || []).filter(({ visible }) => visible);
        setVisibleIndices(imagesToShow.map(({ index }) => index));

        // Download images that both: should be visible and are not already loaded or in flight
        const imagesThatNeedToBeDownloaded = (presentationItems || [])
            .filter(({ index }) => imagesToShow.map((item) => item.index).includes(index))
            .filter(
                ({ itemName }) =>
                    !loadedImagesRef.current.find(
                        (loadedImage) => loadedImage.itemName === itemName
                    )
            )
            .filter(({ itemName }) => !pendingDownloadsRef.current.has(itemName));

        imagesThatNeedToBeDownloaded.forEach((item) => {
            pendingDownloadsRef.current.add(item.itemName);
            loadBlob(item)
                .then((blob) => {
                    // Resolved after unmount: the cleanup already revoked
                    // everything it could see, so a URL created now would
                    // leak for the life of the document. Drop the blob.
                    if (disposedRef.current) return;
                    // Slide deleted while the download was in flight: drop the
                    // blob before creating a URL, or the ghost slide would
                    // reappear until the next room event evicts it.
                    const latest = latestItemsRef.current.find(
                        (candidate) => candidate.itemName === item.itemName
                    );
                    if (!latest) return;
                    const burl = window.URL.createObjectURL(blob);
                    // Use the latest metadata, not the stale request's: a slide
                    // hidden mid-flight stays cached but out of the slide map.
                    const downloadedItem = { ...latest, burl };
                    const existingIndex = loadedImagesRef.current.findIndex(
                        (loadedImage) => loadedImage.itemName === item.itemName
                    );
                    if (existingIndex === -1) {
                        loadedImagesRef.current = loadedImagesRef.current.concat(downloadedItem);
                    } else {
                        // Superseded by a duplicate/retried download for the same item -
                        // replace it and revoke the old blob URL instead of leaking it.
                        const superseded = loadedImagesRef.current[existingIndex];
                        if (superseded.burl && superseded.burl !== burl) {
                            window.URL.revokeObjectURL(superseded.burl);
                        }
                        loadedImagesRef.current = loadedImagesRef.current.map((loadedImage, i) =>
                            i === existingIndex ? downloadedItem : loadedImage
                        );
                    }
                    setTriggerNewSlidesMap(Math.random());
                })
                .catch((e) => {
                    console.error('Failed to load image blob:', item.itemName, e);
                    if (onLoadErrorRef.current) onLoadErrorRef.current(e);
                })
                .finally(() => {
                    pendingDownloadsRef.current.delete(item.itemName);
                });
        });

        // For every image that is already downloaded: if it's still present, update its
        // states (selected/visible/etc); if it's been deleted (no longer present at all
        // in the incoming list), evict it and revoke its blob URL so it doesn't leak.
        loadedImagesRef.current = loadedImagesRef.current.reduce((acc, currentImage) => {
            const match = presentationItems.find(
                (item) => item.itemName === currentImage.itemName
            );
            if (match) {
                // this fixes bug where deleted images 'come back' if you upload new images after deleting
                acc.push({ ...currentImage, ...match });
            } else if (currentImage.burl) {
                window.URL.revokeObjectURL(currentImage.burl);
            }
            return acc;
        }, []);

        // after images ref is updated, cause a render
        setTriggerNewSlidesMap(Math.random());
    }, []);

    const updateImage = useCallback((img) => {
        if (!img || !img.src) return;
        setCurrentImg((prev) => (prev.src !== img.src ? img : prev));
    }, []);

    useEffect(() => {
        const newSlidesMap = loadedImagesRef.current
            // this filter takes away images that are erased
            .filter(({ visible }) => visible)
            .reduce((map, loadedImage) => {
                map[loadedImage.index] = loadedImage;
                return map;
            }, {});
        setSlidesMap(newSlidesMap);
        setLoadingSlides(
            latestItemsRef.current.filter(
                ({ visible, itemName }) =>
                    visible &&
                    !loadedImagesRef.current.some((loadedImage) => loadedImage.itemName === itemName)
            )
        );
    }, [triggerNewSlidesMap]);

    const getPresentationLoadedProgress = () => {
        const totalImages = visibleIndices.length;
        const loadedImages = visibleIndices.filter((index) => !!slidesMap[index]).length;
        return { totalImages, loadedImages };
    };

    // Whenever the slide map changes, point the Vyur at the selected slide,
    // or at a placeholder while loading / before anything is selected.
    useEffect(() => {
        const img = document.createElement('img');
        if (Object.values(slidesMap).length) {
            const selected = Object.values(slidesMap).find(({ selected }) => selected);
            const progress = getPresentationLoadedProgress();
            // The selected slide wins as soon as it has downloaded. Waiting for
            // every slide meant that any upload mid-presentation swapped the
            // whole room's view to the loading image until it downloaded.
            if (selected?.burl) {
                img.src = selected.burl;
                updateImage(img);
            } else if (progress.loadedImages < progress.totalImages) {
                img.src = placeholderImages.loading; // "Grabbing your presentation..."
                updateImage(img);
            } else {
                img.src = placeholderImages.selectImage; // "Select an image"
                updateImage(img);
            }
        } else {
            img.src = placeholderImages.upload; // "Upload an image to get started"
            updateImage(img);
        }
    }, [slidesMap]);

    const getSlides = () =>
        Object.values(slidesMap).sort((a, b) => (sortAsc ? a.index - b.index : b.index - a.index));

    // Optimistically marks the given slide as selected locally so the nav
    // buttons react immediately, instead of waiting for the backend to echo
    // the new active slide back through the event stream (~1s round-trip).
    const selectSlideLocally = (targetIndex) => {
        loadedImagesRef.current = loadedImagesRef.current.map((image) => ({
            ...image,
            selected: image.index === targetIndex
        }));
        setSlidesMap((prev) =>
            Object.entries(prev).reduce((map, [key, slide]) => {
                map[key] = { ...slide, selected: slide.index === targetIndex };
                return map;
            }, {})
        );
    };

    return {
        setItems,
        slidesMap,
        currentImg,
        updateImage,
        getSlides,
        loadingSlides,
        sortAsc,
        toggleSort: () => setSortAsc(!sortAsc),
        maxIndex,
        selectSlideLocally,
        getPresentationLoadedProgress
    };
}
